import { CollapsibleSection } from "components/collapsibleSection";
import { CopyTextParagraph } from "components/copyTextParagraph";
import { Tag } from "components/tag";
import { IAttractionParticipantDetail } from "interfaces";
import { Link } from "react-router-dom";
import { getFormattedDate, getFormattedDateTime } from "utills/helpers";

export const InformationSection = ({
  participantDetailData,
}: {
  participantDetailData: IAttractionParticipantDetail | null;
}) => {
  return (
    <CollapsibleSection 
      label="Information"
      defaultOpen={true}
      additionalLabel={(
        <Tag text={participantDetailData?.participant.source ?? ""} customClassName="capitalize" type="blue" />
      )}
    >
      <div
        className="grid gap-x-4 gap-y-6 px-4 py-4 text-sm sm:px-6 grid-cols-12 lg:gap-x-6"
        style={{ wordBreak: "break-word" }}
      >
        <div className="grid gap-x-4 grid-cols-6 gap-y-6 col-span-full sm:col-span-6 sm:order-1">
          <div className="col-span-2">
            <p className="font-medium">Customer code</p>
          </div>
          <div className="col-span-4">
            <p>{participantDetailData?.participant.customer_id}</p>
          </div>
        </div>
        <div className="grid gap-x-4 grid-cols-6 gap-y-6 col-span-full sm:col-span-6 sm:order-3">
          <div className="col-span-2">
            <p className="font-medium">Participant ID</p>
          </div>
          <div className="col-span-4">
            <p>{participantDetailData?.participant.id}</p>
          </div>
        </div>
        <div className="grid gap-x-4 grid-cols-6 gap-y-6 col-span-full sm:col-span-6 sm:order-5">
          <div className="col-span-2">
            <p className="font-medium">Seat</p>
          </div>
          <div className="col-span-4">
            <p>{participantDetailData?.participant.seat_number}</p>
          </div>
        </div>
        <div className="grid gap-x-4 grid-cols-6 gap-y-6 col-span-full sm:col-span-6 sm:order-7">
          <div className="col-span-2">
            <p className="font-medium">E-ticket usage date</p>
          </div>
          <div className="col-span-4">
            <p>
              {getFormattedDate(
                participantDetailData?.participant.ticket_use_for_date ?? ""
              )}
              {participantDetailData?.participant?.showtime
                ? ` - ${participantDetailData?.participant.showtime}`
                : ""}
            </p>
          </div>
        </div>
        <div className="grid gap-x-4 grid-cols-6 gap-y-6 col-span-full sm:col-span-6 sm:order-9">
          <div className="col-span-2">
            <p className="font-medium">Order code</p>
          </div>
          <div className="col-span-4">
            <Link
              to={`/orders/${participantDetailData?.participant.parent_ref_id}`}
              className="w-fit font-medium text-brand-500 hover:underline"
            >
              {participantDetailData?.participant.parent_ref_id}
            </Link>
          </div>
        </div>
        <div className="grid gap-x-4 grid-cols-6 gap-y-6 col-span-full sm:col-span-6 sm:order-11">
          <div className="col-span-2">
            <p className="font-medium">Full name</p>
          </div>
          <div className="col-span-4">
            <p>{participantDetailData?.participant.full_name}</p>
          </div>
        </div>
        <div className="grid gap-x-4 grid-cols-6 gap-y-6 col-span-full sm:col-span-6 sm:order-[13]">
          <div className="col-span-2">
            <p className="font-medium">Participant template name</p>
          </div>
          <div className="col-span-4">
            <CopyTextParagraph
              content={
                participantDetailData?.participant.attendant_template_name ?? ""
              }
              copyText={
                participantDetailData?.participant.attendant_template_id ?? ""
              }
              hint={
                participantDetailData?.participant.attendant_template_id ?? ""
              }
            />
          </div>
        </div>
        <div className="grid gap-x-4 grid-cols-6 gap-y-6 col-span-full sm:col-span-6 sm:order-2">
          <div className="col-span-2">
            <p className="font-medium">Event Name</p>
          </div>
          <div className="col-span-4">
            <p>{participantDetailData?.participant.event_name_en}</p>
          </div>
        </div>
        <div className="grid gap-x-4 grid-cols-6 gap-y-6 col-span-full sm:col-span-6 sm:order-4">
          <div className="col-span-2">
            <p className="font-medium">Ticket Name</p>
          </div>
          <div className="col-span-4">
            <CopyTextParagraph
              content={participantDetailData?.participant.ticket_name_en ?? ""}
              copyText={participantDetailData?.participant.product_id ?? ""}
              hint={participantDetailData?.participant.product_id ?? ""}
            />
          </div>
        </div>
        <div className="grid gap-x-4 grid-cols-6 gap-y-6 col-span-full sm:col-span-6 sm:order-6">
          <div className="col-span-2">
            <p className="font-medium">Ticket No</p>
          </div>
          <div className="col-span-4">
            <p>{participantDetailData?.participant.ticket_no}</p>
          </div>
        </div>
        <div className="grid gap-x-4 grid-cols-6 gap-y-6 col-span-full sm:col-span-6 sm:order-8">
          <div className="col-span-2">
            <p className="font-medium">Status</p>
          </div>
          <div className="col-span-4">
            <Tag
              text={
                participantDetailData?.participant.is_active
                  ? "Active"
                  : "Inactive"
              }
              type={
                participantDetailData?.participant.is_active ? "green" : "pink"
              }
            />
          </div>
        </div>
        <div className="grid gap-x-4 grid-cols-6 gap-y-6 col-span-full sm:col-span-6 sm:order-10">
          <div className="col-span-2">
            <p className="font-medium">Category</p>
          </div>
          <div className="col-span-4">
            <p>{participantDetailData?.participant.category_en}</p>
          </div>
        </div>
        <div className="grid gap-x-4 grid-cols-6 gap-y-6 col-span-full sm:col-span-6 sm:order-12">
          <div className="col-span-2">
            <p className="font-medium">Note</p>
          </div>
          <div className="col-span-4">
            <p className="whitespace-pre-wrap">
              {participantDetailData?.participant.note}
            </p>
          </div>
        </div>
        <div className="grid gap-x-4 grid-cols-6 gap-y-6 col-span-full sm:col-span-6 sm:order-[14]">
          <div className="col-span-2">
            <p className="font-medium">Created Date</p>
          </div>
          <div className="col-span-4">
            <p className="whitespace-pre-wrap">
              {getFormattedDateTime(participantDetailData?.participant.created_at ?? "")}
            </p>
          </div>
        </div>
        {participantDetailData?.participant.reference_code ? (
          <div className="grid gap-x-4 grid-cols-6 gap-y-6 col-span-full sm:col-span-6 sm:order-[15]">
            <div className="col-span-2">
              <p className="font-medium">Reference Code</p>
            </div>
            <div className="col-span-4">
              <p className="whitespace-pre-wrap">
                {participantDetailData?.participant.reference_code}
              </p>
            </div>
          </div>
        ) : null}
      </div>
    </CollapsibleSection>
  );
};
